import type { EDevice } from '@/constants';
import type { CartLineItem } from '@/modules/cart/domain/CartLineItem';
import type { CartOutOfStockItem } from '@/modules/cart/domain/CartOutOfStockItem';
import type { CartProductOption } from '@/modules/cart/domain/CartProductOption';
import type { Payment } from '@/modules/perseo-billing/interfaces';
import type { ClubType } from '@/modules/perseo-billing/interfaces/src/bff-web/billing';
import type { IdentificationDocumentType } from '@/modules/shared/types/IdentificationDocumentType';
import type { PriceDto, PriceValueDto } from '@/modules/shared/types/PriceDto';
import type {
  CollectNodeAddressDto,
  ECollectNodeAdmin
} from '@/modules/shippingMethod/infrastructure/dto/HttpShippingMethodDto.ts';

//TODO: Improve this interface
export interface Cart {
  id: string;
  customerId?: string;
  anonymousId?: string;
  lineItems: CartLineItem[];
  productOptions: CartProductOption[];
  deliveryInfo?: DeliveryInfo;
  totalLineItemQuantity: number;
  totalPrice?: PriceValueDto;
  taxedPrice?: TaxedPrice;
  shippingAddress?: Address;
  billingAddress?: Address;
  discountCodes?: DiscountCodeInfo[];
  directDiscounts?: [];
  discountOnTotalPrice?: DiscountOnTotalPrice;
  customerTags?: [];
  source?: string | EDevice;
  outOfStockItems?: CartOutOfStockItem[];
  lastOutOfStockItemsRemoved?: string[];
  payments?: Payment[];
  clubParisCode?: string;
  clubParisCodeType?: ClubType;
  weddingEmail1?: string;
  weddingEmail2?: string;
  weddingId?: string;
  weddingItsGift?: boolean;
  weddingMessage?: string;
  weddingFirstName1?: string;
  weddingFirstName2?: string;
  customCartType?: string;
}

export interface LineItemDraft {
  sku: string;
  quantity: number;
  warrantyId?: string;
  serviceIds?: string[];
}

export interface DeliveryInfo {
  id: string;
  totalPrice: PriceDto;
  taxedPrice: TaxedPrice;
  includedDiscounts: IncludedDiscounts[];
  deliveryGroups: DeliveryGroup[];
}

export interface IncludedDiscounts {
  discount: {
    id: string;
  };
  discountedAmount: PriceValueDto;
  name: LocaleString;
  description: LocaleString;
}
export interface DeliveryGroup {
  id: string;
  products: string[];
  lineItemsInDeliveryGroup?: number;
  shippingOption: ShippingOption;
  shippingOptions?: ShippingOption[];
  optionSelected: OptionSelected;
  totalPrice: PriceValueDto;
  taxedPrice: PriceValueDto;
  proratedCost: PriceValueDto;
  includedDiscounts: IncludedDiscounts[];

  // Pickup person authorization fields
  collectOnBehalfName?: string;
  collectOnBehalfDocumentType?: IdentificationDocumentType;
  collectOnBehalfDocumentID?: string;

  // @deprecated - Keep for backward compatibility
  collectOnBehalfRut?: string;
}

export enum EShippingType {
  COLLECT = 'COLLECT',
  DELIVERY = 'DELIVERY'
}

export enum EShippingMethod {
  SCHEDULED = 'SCHEDULED',
  NEXT_DAY = 'NEXT_DAY',
  SAME_DAY = 'SAME_DAY',
  DATE_RANGE = 'DATE_RANGE',
  SCHEDULED_DEFERRED = 'SCHEDULED_DEFERRED'
}

export enum EShippingSubType {
  HOME = 'HOME',
  IN_STORE = 'IN_STORE',
  COPEC = 'COPEC'
}

export interface ShippingOption {
  selected: boolean;
  shippingSubType: EShippingSubType | 'HOME' | 'IN_STORE' | 'COPEC';
  shippingMethod:
    | EShippingMethod
    | 'SCHEDULED'
    | 'NEXT_DAY'
    | 'SAME_DAY'
    | 'DATE_RANGE'
    | 'SCHEDULED_DEFERRED';
  logisticsModel: string;
  shipmentCostTotal?: string;
  shipmentCostBase?: string;
  shipmentCostCurrency: string;
  shippingType: EShippingType | 'COLLECT' | 'DELIVERY';
  options?: OptionSelected[];
  originNodeType?: 'PROVIDER' | 'WAREHOUSE' | 'STORE' | 'SELLER';
  originNodeCode?: string;
  originNodeAdmin?: string;
  shippingCapability?: string;
  geoName?: string;
  uniqueReferences?: {
    EOM?: string;
    MKP?: string;
  };
}

export interface OptionSelected {
  selected?: boolean;
  shippingSubType?: 'HOME' | 'IN_STORE' | 'COPEC';
  originNodeType?: 'PROVIDER' | 'WAREHOUSE' | 'STORE' | 'SELLER';
  originNodeCode?: string;
  originNodeAdmin?: string;
  shippingCapability?: string;
  collectNodeCode?: string;
  collectNodeType?: string;
  collectNodeAdmin?: ECollectNodeAdmin;
  collectNodeName?: string;
  collectNodeAddress?: CollectNodeAddressDto;
  shippingType?: 'DELIVERY' | 'COLLECT';
  shippingMethod?:
    | 'SCHEDULED'
    | 'NEXT_DAY'
    | 'SAME_DAY'
    | 'DATE_RANGE'
    | 'SCHEDULED_DEFERRED';
  hasCommuneOption?: boolean;
  rank?: number;
  lineItemsInNode?: number;
  deliveryDateFrom: string;
  hash?: string;
  deliveryDateTo: string;
  shipDate: string;
  timeSlot?: TimeSlot;
}

export interface TimeSlot {
  name: string;
  deliveryTimeStart: string;
  deliveryTimeEnd: string;
}

export interface DiscountCode {
  id: string;
  typeId?: string;
  obj?: Discount;
}

export interface DiscountCodeInfo {
  discountCode: DiscountCode;
  state: string;
}

export interface DiscountOnTotalPrice {
  includedDiscounts: [
    {
      discount: { typeId: string; id: string };
      discountedAmount: PriceDto;
    }
  ];
}

export interface Discount {
  cartDiscounts: [{ typeId: 'cart-discount'; id: string }];
  code?: string;
  id: string;
  isActive: boolean;
  name: LocaleString;
  description: LocaleString;
}

export interface LocaleString {
  'es-CL': string;
}

export interface Prices {
  regular: Price;
  offer: Price;
  paymentMethod: Price;
}

export interface Regular {
  id: string;
  value: PriceValueDto;
}

export interface Offer {
  id: string;
  key: string;
  value: PriceValueDto;
  discountOnRegular: number;
}

export interface PaymentMethod {
  discountOnRegular: number;
  id: string;
  value: PriceValueDto;
  method: string;
}

export interface Attribute {
  name: string;
  value: string | string[];
}

export interface Image {
  url: string;
  label: string;
  dimensions: Dimensions;
}

export interface Dimensions {
  w: number;
  h: number;
}

export interface Price {
  id: string;
  key: string;
  value: PriceValueDto;
  channel: Channel;
}

export interface Channel {
  typeId: string;
  id: string;
  obj: Obj;
}

export interface Obj {
  id: string;
  version: number;
  versionModifiedAt: string;
  createdAt: string;
  lastModifiedAt: string;
  key: string;
  roles: string[];
  name: LocaleString;
  description: LocaleString;
}

export interface TaxedPrice {
  totalNet: PriceValueDto;
  totalGross: PriceValueDto;
  totalTax: PriceValueDto;
}

export interface Address {
  id: number;
  region: number;
  customerRut?: string;
  commune: string;
  streetName?: string;
  streetNumber?: string;
  apartment?: string;
  alias: string;
  email?: string;
  additionalAddressInfo?: string;
  firstName: string;
  lastName: string;
  phone: string;
  isDefault?: boolean;
  isInvoice?: boolean;
  companyBusinessName?: string;
  companyEmail?: string;
  companyIdentityDocumentNumber?: string;
  companyLineOfBusiness?: string;
  companyName?: string;
  documentType?: IdentificationDocumentType;
  documentID?: string;
}

export interface GiftCardFormFields {
  cardNumber: string;
  cardCvv: string;
}

export interface ClubParisFields {
  clubParisCode: string;
}

export interface AuthorizedPerson {
  fullname?: string;

  // New fields
  documentType?: IdentificationDocumentType;
  documentID?: string;

  // @deprecated - Keep for backward compatibility
  rut?: string;
}

export interface InvoiceFormFields {
  country?: string;
  alias?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  streetName?: string;
  streetNumber?: string;
  region?: string;
  regionName?: string;
  commune?: string;
  communeName?: string;
  apartment?: string;
  phone?: string;
  additionalAddressInfo?: string;
  latitude?: number;
  longitude?: number;
  customerRut?: string;
  isInvoice?: boolean;
  companyLineOfBusiness?: string;
  companyEmail?: string;
  companyName?: string;
  companyIdentityDocumentNumber?: string;
  companyBusinessName?: string;
}

export enum EDiscoutCodeState {
  MatchesCart = 'MatchesCart',
  DoesNotMatchCart = 'DoesNotMatchCart',
  DiscountCodeNonApplicable = 'DiscountCodeNonApplicable'
}
